/**
 * Content/exams/e1-sbi-po.ts
 * Content for SBI PO (e1)
 * IDs reference Content/ids.ts
 */

// ─── Subtopic Resource Types ──────────────────────────────────────────────────

export interface StudyNotePage {
  id: string;
  title: string;
}

export interface MockTestRef {
  id: string;
  name: string;
}

export interface SubtopicResources {
  studyNotes: StudyNotePage[];
  pyqCount: number;
  practiceCount: number;
  mockTests: MockTestRef[];
}

export const examId = "e1";
export const examName = "SBI PO";

export const content = {
  examId,
  examName,
  stages: [
    {
      stageId: "st1", // Prelims
      sections: [
        {
          sectionId: "sec1", // Quantitative Aptitude
          topics: [
            {
              topicId: "t1", // Number System
              subtopics: ["subt1", "subt2", "subt3", "subt4", "subt5", "subt6"],
              weightage: "5 – 8%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt1: {
                  studyNotes: [
                    { id: "sn-subt1-1", title: "Natural & Whole Numbers" },
                    { id: "sn-subt1-2", title: "Integers & Rational Numbers" },
                    { id: "sn-subt1-3", title: "Prime & Composite Numbers" },
                  ],
                  pyqCount: 8,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-ns-01", name: "Number Series — Topic Test 01" },
                  ],
                },
                subt2: {
                  studyNotes: [
                    { id: "sn-subt2-1", title: "LCM Concepts & Methods" },
                    { id: "sn-subt2-2", title: "HCF Concepts & Methods" },
                    { id: "sn-subt2-3", title: "LCM & HCF Applications" },
                    { id: "sn-subt2-4", title: "LCM & HCF Shortcuts" },
                  ],
                  pyqCount: 12,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-ns-01", name: "Number Series — Topic Test 01" },
                    { id: "sbi-po-mock-sec-qa-01", name: "Quantitative Aptitude — Sectional Test 01" },
                  ],
                },
                subt3: {
                  studyNotes: [
                    { id: "sn-subt3-1", title: "Divisibility Rules (2–13)" },
                    { id: "sn-subt3-2", title: "Divisibility Tricks & Shortcuts" },
                  ],
                  pyqCount: 10,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-qa-01", name: "Quantitative Aptitude — Sectional Test 01" },
                  ],
                },
                subt4: {
                  studyNotes: [
                    { id: "sn-subt4-1", title: "Fractions Basics" },
                    { id: "sn-subt4-2", title: "Decimals & Conversions" },
                  ],
                  pyqCount: 6,
                  practiceCount: 5,
                  mockTests: [],
                },
                subt5: {
                  studyNotes: [
                    { id: "sn-subt5-1", title: "Surds & Indices Basics" },
                    { id: "sn-subt5-2", title: "Laws of Indices" },
                    { id: "sn-subt5-3", title: "Simplification of Surds" },
                  ],
                  pyqCount: 9,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-qa-01", name: "Quantitative Aptitude — Sectional Test 01" },
                  ],
                },
                subt6: {
                  studyNotes: [
                    { id: "sn-subt6-1", title: "Unit Digit & Cyclicity" },
                    { id: "sn-subt6-2", title: "Remainder Theorem" },
                    { id: "sn-subt6-3", title: "Number Series Patterns" },
                  ],
                  pyqCount: 15,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-ns-01", name: "Number Series — Topic Test 01" },
                    { id: "sbi-po-mock-sec-qa-01", name: "Quantitative Aptitude — Sectional Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Number System covers Natural Numbers, Whole Numbers, Integers, Rational and Irrational Numbers, LCM and HCF, Divisibility Rules, Fractions and Decimals, Surds and Indices, Number Series and Sequences, Unit Digit Concepts, Cyclicity of Numbers, and Remainder Theorem.",
              keyPointsToFocus: [
                "Master LCM & HCF shortcut methods for quick calculation",
                "Learn all divisibility rules (2 through 13) for fast elimination",
                "Practice number series pattern recognition under time pressure",
                "Understand cyclicity of unit digits for power-based questions",
              ],
              importantSubtopics: ["subt2", "subt3", "subt6"],
            },
            {
              topicId: "t12", // Data Interpretation
              subtopics: ["subt26", "subt27", "subt28", "subt29", "subt30"],
              weightage: "15 – 20%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt26: {
                  studyNotes: [
                    { id: "sn-subt26-1", title: "Bar Graph Interpretation" },
                    { id: "sn-subt26-2", title: "Reading Bar Charts Quickly" },
                  ],
                  pyqCount: 20,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-di-01", name: "Data Interpretation — Topic Test 01" },
                    { id: "sbi-po-mock-sec-qa-01", name: "Quantitative Aptitude — Sectional Test 01" },
                  ],
                },
                subt27: {
                  studyNotes: [
                    { id: "sn-subt27-1", title: "Line Graph Analysis" },
                    { id: "sn-subt27-2", title: "Trend Identification" },
                  ],
                  pyqCount: 15,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-di-01", name: "Data Interpretation — Topic Test 01" },
                  ],
                },
                subt28: {
                  studyNotes: [
                    { id: "sn-subt28-1", title: "Pie Chart Basics" },
                    { id: "sn-subt28-2", title: "Percentage Calculations in Pie Charts" },
                    { id: "sn-subt28-3", title: "Pie Chart Shortcuts" },
                  ],
                  pyqCount: 18,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-di-01", name: "Data Interpretation — Topic Test 01" },
                    { id: "sbi-po-mock-sec-qa-01", name: "Quantitative Aptitude — Sectional Test 01" },
                  ],
                },
                subt29: {
                  studyNotes: [
                    { id: "sn-subt29-1", title: "Tabular DI Approach" },
                    { id: "sn-subt29-2", title: "Multi-row Table Analysis" },
                  ],
                  pyqCount: 22,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-di-01", name: "Data Interpretation — Topic Test 01" },
                  ],
                },
                subt30: {
                  studyNotes: [
                    { id: "sn-subt30-1", title: "Mixed DI (Bar + Pie)" },
                    { id: "sn-subt30-2", title: "Mixed DI (Table + Line)" },
                    { id: "sn-subt30-3", title: "Caselet DI" },
                  ],
                  pyqCount: 25,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-di-01", name: "Data Interpretation — Topic Test 01" },
                    { id: "sbi-po-mock-sec-qa-01", name: "Quantitative Aptitude — Sectional Test 01" },
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Data Interpretation includes Bar Graphs, Line Graphs, Pie Charts, Tabular Data, Mixed Charts, Percentage-based DI, Ratio-based DI, Average and Mean from data sets, Comparison across multiple data sets, and Two-variable DI problems.",
              keyPointsToFocus: [
                "Practice reading graphs quickly and extracting key values",
                "Focus on percentage change and ratio comparison in DI sets",
                "Attempt DI sets in under 6 minutes for Prelims timing",
                "Mixed DI (bar + table) is the most common format in SBI PO",
              ],
              importantSubtopics: ["subt26", "subt28", "subt30"],
            },
            {
              topicId: "t3", // Percentage
              subtopics: ["subt9", "subt10", "subt11"],
              weightage: "5 – 8%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt9: {
                  studyNotes: [
                    { id: "sn-subt9-1", title: "Percentage Basics" },
                    { id: "sn-subt9-2", title: "Fraction-to-Percentage Equivalents" },
                    { id: "sn-subt9-3", title: "Percentage Increase & Decrease" },
                  ],
                  pyqCount: 12,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-qa-01", name: "Quantitative Aptitude — Sectional Test 01" },
                  ],
                },
                subt10: {
                  studyNotes: [
                    { id: "sn-subt10-1", title: "Successive Percentage Change" },
                    { id: "sn-subt10-2", title: "Reverse Percentage" },
                  ],
                  pyqCount: 10,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-qa-01", name: "Quantitative Aptitude — Sectional Test 01" },
                  ],
                },
                subt11: {
                  studyNotes: [
                    { id: "sn-subt11-1", title: "Percentage in Profit & Loss" },
                    { id: "sn-subt11-2", title: "Percentage in DI" },
                  ],
                  pyqCount: 8,
                  practiceCount: 5,
                  mockTests: [],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Percentage covers Basic Percentage Calculations, Percentage Increase and Decrease, Percentage Change Formula, Population Growth using Percentage, Percentage in Profit and Loss, Percentage in Data Interpretation, Successive Percentage Change, and Reverse Percentage Problems.",
              keyPointsToFocus: [
                "Memorise fraction-to-percentage equivalents for speed",
                "Understand successive percentage change formula",
                "Percentage is embedded in DI — treat it as a combined topic",
                "Practice reverse percentage (finding original value) problems",
              ],
              importantSubtopics: ["subt9", "subt10"],
            },
            {
              topicId: "t9", // Time & Work
              subtopics: ["subt19", "subt20", "subt21"],
              weightage: "5 – 8%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt19: {
                  studyNotes: [
                    { id: "sn-subt19-1", title: "Work Efficiency Concepts" },
                    { id: "sn-subt19-2", title: "LCM Method for Time & Work" },
                    { id: "sn-subt19-3", title: "Combined Work Problems" },
                  ],
                  pyqCount: 10,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-qa-01", name: "Quantitative Aptitude — Sectional Test 01" },
                  ],
                },
                subt20: {
                  studyNotes: [
                    { id: "sn-subt20-1", title: "Pipes & Cisterns" },
                    { id: "sn-subt20-2", title: "Inlet & Outlet Problems" },
                  ],
                  pyqCount: 8,
                  practiceCount: 5,
                  mockTests: [],
                },
                subt21: {
                  studyNotes: [
                    { id: "sn-subt21-1", title: "Work & Wages" },
                    { id: "sn-subt21-2", title: "Man-Days Concept" },
                    { id: "sn-subt21-3", title: "Efficiency-based Comparison" },
                  ],
                  pyqCount: 12,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-qa-01", name: "Quantitative Aptitude — Sectional Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Time and Work covers Work Efficiency Concepts, Work Done in Given Time, Combined Work Problems, Pipes and Cisterns, Work and Wages, Alternate Day Work Problems, Man-Days Concept, and Efficiency-based Comparison Problems.",
              keyPointsToFocus: [
                "Use LCM method for Time & Work to avoid fractions",
                "Pipes & Cisterns is the same concept — practice both together",
                "Work and Wages problems appear frequently in SBI PO",
                "Efficiency-based questions require ratio understanding",
              ],
              importantSubtopics: ["subt19", "subt21"],
            },
          ],
        },
        {
          sectionId: "sec2", // Reasoning Ability
          topics: [
            {
              topicId: "t27", // Puzzles
              subtopics: ["subt37", "subt38", "subt39"],
              weightage: "15 – 20%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt37: {
                  studyNotes: [
                    { id: "sn-subt37-1", title: "Floor-based Puzzles" },
                    { id: "sn-subt37-2", title: "Box-based Puzzles" },
                    { id: "sn-subt37-3", title: "Scheduling Puzzles" },
                  ],
                  pyqCount: 20,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-puzzle-01", name: "Puzzles & Seating — Topic Test 01" },
                    { id: "sbi-po-mock-sec-ra-01", name: "Reasoning Ability — Sectional Test 01" },
                  ],
                },
                subt38: {
                  studyNotes: [
                    { id: "sn-subt38-1", title: "Designation-based Puzzles" },
                    { id: "sn-subt38-2", title: "Multi-variable Puzzles" },
                  ],
                  pyqCount: 18,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-puzzle-01", name: "Puzzles & Seating — Topic Test 01" },
                  ],
                },
                subt39: {
                  studyNotes: [
                    { id: "sn-subt39-1", title: "Age-based Puzzles" },
                    { id: "sn-subt39-2", title: "Comparison Puzzles" },
                  ],
                  pyqCount: 15,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-puzzle-01", name: "Puzzles & Seating — Topic Test 01" },
                    { id: "sbi-po-mock-sec-ra-01", name: "Reasoning Ability — Sectional Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Puzzles include Floor-based Puzzles, Box-based Puzzles, Day/Month/Year Scheduling Puzzles, Designation-based Puzzles, Multi-variable Puzzles combining Floor and Profession, Age-based Puzzles, and Comparison-based Puzzles involving multiple attributes.",
              keyPointsToFocus: [
                "Expect 2–3 puzzle sets of 5 questions each in Prelims",
                "Draw a clear table/grid before solving any puzzle",
                "Multi-variable puzzles (floor + profession) are most common",
                "Practice solving puzzles in under 7 minutes per set",
              ],
              importantSubtopics: ["subt37", "subt38", "subt39"],
            },
            {
              topicId: "t26", // Seating Arrangement
              subtopics: ["subt35", "subt36"],
              weightage: "10 – 15%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt35: {
                  studyNotes: [
                    { id: "sn-subt35-1", title: "Linear Seating Arrangement" },
                    { id: "sn-subt35-2", title: "Double-row Linear Arrangement" },
                  ],
                  pyqCount: 16,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-puzzle-01", name: "Puzzles & Seating — Topic Test 01" },
                    { id: "sbi-po-mock-sec-ra-01", name: "Reasoning Ability — Sectional Test 01" },
                  ],
                },
                subt36: {
                  studyNotes: [
                    { id: "sn-subt36-1", title: "Circular Seating Arrangement" },
                    { id: "sn-subt36-2", title: "Mixed Arrangements" },
                    { id: "sn-subt36-3", title: "Seating + Profession Combos" },
                  ],
                  pyqCount: 14,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-puzzle-01", name: "Puzzles & Seating — Topic Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Seating Arrangement covers Linear Seating Arrangement (single row, double row, facing each other), Circular Seating Arrangement (facing centre, facing outward, mixed), and Complex Arrangements combining seating with professions or other attributes.",
              keyPointsToFocus: [
                "Linear and circular arrangements are both equally important",
                "Double-row linear arrangements are a common SBI PO variant",
                "Always mark direction (facing in/out) clearly in circular puzzles",
                "Mixed arrangements (seating + profession) require extra care",
              ],
              importantSubtopics: ["subt35", "subt36"],
            },
            {
              topicId: "t25", // Inequalities
              subtopics: ["subt40", "subt41"],
              weightage: "5 – 8%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt40: {
                  studyNotes: [
                    { id: "sn-subt40-1", title: "Direct Inequalities" },
                    { id: "sn-subt40-2", title: "Either-Or Cases" },
                  ],
                  pyqCount: 10,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-ra-01", name: "Reasoning Ability — Sectional Test 01" },
                  ],
                },
                subt41: {
                  studyNotes: [
                    { id: "sn-subt41-1", title: "Coded Inequalities" },
                    { id: "sn-subt41-2", title: "Possibility-based Conclusions" },
                  ],
                  pyqCount: 12,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-ra-01", name: "Reasoning Ability — Sectional Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Inequalities covers Direct Inequalities, Coded Inequalities, Combination of Symbols, Either-Or Cases, Complementary Pairs, and Possibility-based Inequality Conclusions.",
              keyPointsToFocus: [
                "Coded and direct inequalities are quick-scoring — aim for 100% accuracy",
                "Learn all symbol combinations and their conclusions",
                "Either-Or cases are tricky — practice them separately",
                "Solve inequalities in under 30 seconds per question",
              ],
              importantSubtopics: ["subt40", "subt41"],
            },
            {
              topicId: "t21", // Syllogism
              subtopics: [],
              weightage: "5 – 8%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                "t21": {
                  studyNotes: [
                    { id: "sn-t21-1", title: "All, Some, No Statements" },
                    { id: "sn-t21-2", title: "Definite & Possibility Conclusions" },
                    { id: "sn-t21-3", title: "Reverse Syllogism" },
                    { id: "sn-t21-4", title: "Negative Statements" },
                  ],
                  pyqCount: 15,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-ra-01", name: "Reasoning Ability — Sectional Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Syllogism covers All, Some, No type Statements, Definite Conclusions, Possibility Conclusions, Reverse Syllogism, Complementary Pairs, and Syllogism with Negative Statements.",
              keyPointsToFocus: [
                "Possibility-based syllogism is the most tested variant in SBI PO",
                "Use Venn diagrams for complex multi-statement syllogisms",
                "Practice reverse syllogism (conclusion given, find statement)",
                "Negative statements (No, Not all) need careful handling",
              ],
              importantSubtopics: [],
            },
          ],
        },
        {
          sectionId: "sec3", // English Language
          topics: [
            {
              topicId: "t35", // Reading Comprehension
              subtopics: ["subt53", "subt54", "subt55"],
              weightage: "20 – 25%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt53: {
                  studyNotes: [
                    { id: "sn-subt53-1", title: "Factual Questions Strategy" },
                    { id: "sn-subt53-2", title: "Inference Questions" },
                    { id: "sn-subt53-3", title: "Vocabulary in Context" },
                  ],
                  pyqCount: 25,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-en-01", name: "English Language — Sectional Test 01" },
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                  ],
                },
                subt54: {
                  studyNotes: [
                    { id: "sn-subt54-1", title: "Author\'s Tone & Attitude" },
                    { id: "sn-subt54-2", title: "Title & Theme Identification" },
                  ],
                  pyqCount: 18,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-en-01", name: "English Language — Sectional Test 01" },
                  ],
                },
                subt55: {
                  studyNotes: [
                    { id: "sn-subt55-1", title: "Passage-based Fill in the Blanks" },
                  ],
                  pyqCount: 10,
                  practiceCount: 5,
                  mockTests: [],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Reading Comprehension covers Factual Questions, Inferential Questions, Vocabulary-based Questions (Synonyms/Antonyms from passage), Tone and Attitude of the Author, Title and Theme Identification, and Passage-based Fill in the Blanks.",
              keyPointsToFocus: [
                "RC passages in Prelims are moderate length — skim first, then answer",
                "Inference-based questions require reading between the lines",
                "Vocabulary questions test contextual meaning, not dictionary meaning",
                "Attempt RC after other English topics to save time",
              ],
              importantSubtopics: ["subt53", "subt54"],
            },
            {
              topicId: "t36", // Cloze Test
              subtopics: [],
              weightage: "10 – 15%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                "t36": {
                  studyNotes: [
                    { id: "sn-t36-1", title: "Contextual Vocabulary" },
                    { id: "sn-t36-2", title: "Grammar-based Blank Filling" },
                    { id: "sn-t36-3", title: "New Pattern Cloze Test" },
                  ],
                  pyqCount: 15,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-en-01", name: "English Language — Sectional Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Cloze Test covers Contextual Vocabulary, Grammar-based Blank Filling, Coherence and Flow of Passage, Preposition and Conjunction Usage, and New Pattern Cloze Test (choosing the correct sentence for a blank).",
              keyPointsToFocus: [
                "Read the full passage before filling any blank",
                "Context determines the correct word — not just grammar",
                "New pattern cloze (sentence-based) requires understanding paragraph flow",
                "Prepositions and conjunctions are the most tested parts",
              ],
              importantSubtopics: [],
            },
            {
              topicId: "t37", // Error Spotting
              subtopics: ["subt56", "subt57", "subt58", "subt59"],
              weightage: "8 – 12%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt56: {
                  studyNotes: [
                    { id: "sn-subt56-1", title: "Subject-Verb Agreement" },
                    { id: "sn-subt56-2", title: "Noun & Pronoun Errors" },
                  ],
                  pyqCount: 12,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-en-01", name: "English Language — Sectional Test 01" },
                  ],
                },
                subt57: {
                  studyNotes: [
                    { id: "sn-subt57-1", title: "Verb & Tense Errors" },
                    { id: "sn-subt57-2", title: "Preposition Errors" },
                  ],
                  pyqCount: 10,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-en-01", name: "English Language — Sectional Test 01" },
                  ],
                },
                subt58: {
                  studyNotes: [
                    { id: "sn-subt58-1", title: "Article Errors" },
                    { id: "sn-subt58-2", title: "Adjective & Adverb Usage" },
                  ],
                  pyqCount: 8,
                  practiceCount: 5,
                  mockTests: [],
                },
                subt59: {
                  studyNotes: [
                    { id: "sn-subt59-1", title: "Parallelism" },
                    { id: "sn-subt59-2", title: "Sentence Correction" },
                  ],
                  pyqCount: 9,
                  practiceCount: 5,
                  mockTests: [],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Error Spotting covers Subject-Verb Agreement, Noun and Pronoun Errors, Verb and Tense Errors, Preposition Errors, Article Errors, Adjective and Adverb Usage, Parallelism, and Sentence Correction variants.",
              keyPointsToFocus: [
                "Subject-verb agreement and tense errors are most frequently tested",
                "Preposition errors require knowing fixed preposition phrases",
                "Article (a/an/the) errors are easy marks — learn the rules",
                "New pattern: identify the correct sentence among four options",
              ],
              importantSubtopics: ["subt56", "subt57"],
            },
          ],
        },
      ],
    },
    {
      stageId: "st2", // Mains
      sections: [
        {
          sectionId: "sec6", // Data Analysis & Interpretation
          topics: [
            {
              topicId: "t12", // Data Interpretation
              subtopics: ["subt29", "subt30", "subt31"],
              weightage: "30 – 35%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt29: {
                  studyNotes: [
                    { id: "sn-subt29-m1", title: "Caselet DI Approach" },
                    { id: "sn-subt29-m2", title: "Paragraph-based Data Extraction" },
                  ],
                  pyqCount: 20,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-di-01", name: "Data Interpretation — Topic Test 01" },
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                  ],
                },
                subt30: {
                  studyNotes: [
                    { id: "sn-subt30-m1", title: "Mixed DI (Bar + Pie)" },
                    { id: "sn-subt30-m2", title: "Mixed DI (Table + Line)" },
                    { id: "sn-subt30-m3", title: "Quantity Comparison" },
                  ],
                  pyqCount: 25,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-di-01", name: "Data Interpretation — Topic Test 01" },
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                    { id: "sbi-po-mock-fl-02", name: "SBI PO Prelims — Mock Test 02" },
                  ],
                },
                subt31: {
                  studyNotes: [
                    { id: "sn-subt31-m1", title: "Data Sufficiency" },
                    { id: "sn-subt31-m2", title: "Arithmetic-based DI" },
                  ],
                  pyqCount: 15,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Data Analysis and Interpretation in Mains covers Complex Bar Graphs, Multi-line Graphs, Caselet DI (paragraph-based data), Mixed Chart DI (bar + pie, table + line), Data Sufficiency, Quantity Comparison, and Arithmetic-based DI combining Profit/Loss, SI/CI, and Ratio within data sets.",
              keyPointsToFocus: [
                "Caselet DI is unique to Mains — practice extracting data from paragraphs",
                "Mixed DI sets combine two chart types — read both carefully before solving",
                "Accuracy is more important than speed in Mains DI",
                "Quantity I vs Quantity II questions require full calculation — no shortcuts",
              ],
              importantSubtopics: ["subt30", "subt31"],
            },
            {
              topicId: "t16", // Probability
              subtopics: [],
              weightage: "5 – 8%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                "t16": {
                  studyNotes: [
                    { id: "sn-t16-1", title: "Basic Probability Concepts" },
                    { id: "sn-t16-2", title: "Conditional Probability" },
                    { id: "sn-t16-3", title: "Dice & Card Problems" },
                    { id: "sn-t16-4", title: "Probability with P&C" },
                  ],
                  pyqCount: 10,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Probability covers Basic Probability Concepts, Classical Probability, Conditional Probability, Independent and Dependent Events, Probability using Permutation and Combination, Dice and Card-based Probability, and Probability in Data Sufficiency.",
              keyPointsToFocus: [
                "Understand the difference between independent and dependent events",
                "Card and dice problems are the most common probability formats",
                "Conditional probability requires careful reading of conditions",
                "Combine with P&C concepts for complex problems",
              ],
              importantSubtopics: [],
            },
          ],
        },
        {
          sectionId: "sec15", // Reasoning & Computer Aptitude
          topics: [
            {
              topicId: "t27", // Puzzles
              subtopics: ["subt37", "subt38", "subt39"],
              weightage: "20 – 25%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt37: {
                  studyNotes: [
                    { id: "sn-subt37-m1", title: "Multi-variable Complex Puzzles" },
                    { id: "sn-subt37-m2", title: "Scheduling Puzzles with Conditions" },
                    { id: "sn-subt37-m3", title: "Nested Puzzles (Floor + Box + Designation)" },
                  ],
                  pyqCount: 22,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-puzzle-01", name: "Puzzles & Seating — Topic Test 01" },
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                    { id: "sbi-po-mock-fl-02", name: "SBI PO Prelims — Mock Test 02" },
                  ],
                },
                subt38: {
                  studyNotes: [
                    { id: "sn-subt38-m1", title: "Comparison Puzzles with Ranking" },
                    { id: "sn-subt38-m2", title: "Uncertain Data Puzzles" },
                  ],
                  pyqCount: 18,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-puzzle-01", name: "Puzzles & Seating — Topic Test 01" },
                  ],
                },
                subt39: {
                  studyNotes: [
                    { id: "sn-subt39-m1", title: "Possibility-based Conclusions" },
                  ],
                  pyqCount: 12,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-top-puzzle-01", name: "Puzzles & Seating — Topic Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Mains Puzzles include Multi-variable Complex Puzzles (3+ attributes), Scheduling Puzzles with Conditions, Nested Puzzles combining Floor, Box, and Designation, Comparison Puzzles with Ranking, and Puzzles with Uncertain Data requiring possibility-based conclusions.",
              keyPointsToFocus: [
                "Mains puzzles have 3+ variables — build a structured grid",
                "Spend no more than 8 minutes per puzzle set",
                "Uncertain data puzzles require checking all possible cases",
                "Practice multi-variable puzzles daily in the last 2 months",
              ],
              importantSubtopics: ["subt37", "subt38", "subt39"],
            },
            {
              topicId: "t55", // Computer Fundamentals
              subtopics: ["subt71", "subt72", "subt76", "subt77"],
              weightage: "10 – 15%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt71: {
                  studyNotes: [
                    { id: "sn-subt71-1", title: "Hardware & Software Concepts" },
                    { id: "sn-subt71-2", title: "Operating System Basics" },
                    { id: "sn-subt71-3", title: "Memory Types (RAM, ROM, Cache)" },
                  ],
                  pyqCount: 12,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                  ],
                },
                subt72: {
                  studyNotes: [
                    { id: "sn-subt72-1", title: "Input/Output Devices" },
                    { id: "sn-subt72-2", title: "Internet & Networking Fundamentals" },
                    { id: "sn-subt72-3", title: "Network Types (LAN, WAN, MAN)" },
                  ],
                  pyqCount: 10,
                  practiceCount: 5,
                  mockTests: [],
                },
                subt76: {
                  studyNotes: [
                    { id: "sn-subt76-1", title: "Computer Shortcuts" },
                    { id: "sn-subt76-2", title: "MS Office Basics" },
                  ],
                  pyqCount: 8,
                  practiceCount: 5,
                  mockTests: [],
                },
                subt77: {
                  studyNotes: [
                    { id: "sn-subt77-1", title: "Logic-based Computer Aptitude" },
                  ],
                  pyqCount: 6,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Computer Aptitude covers Hardware and Software Concepts, Operating System Basics, Memory Types (RAM, ROM, Cache), Input/Output Devices, Internet and Networking Fundamentals, Network Types (LAN, WAN, MAN), Computer Shortcuts and MS Office Basics, and Logic-based Computer Aptitude Questions.",
              keyPointsToFocus: [
                "Computer Aptitude in Mains includes logic-based questions, not just theory",
                "Focus on networking concepts — LAN, WAN, protocols",
                "MS Office shortcuts and functions are frequently asked",
                "Operating system concepts (multitasking, memory management) are important",
              ],
              importantSubtopics: ["subt71", "subt72"],
            },
          ],
        },
        {
          sectionId: "sec16", // General/Economy/Banking Awareness
          topics: [
            {
              topicId: "t44", // Banking & Finance
              subtopics: ["subt66", "subt67", "subt68", "subt69", "subt70"],
              weightage: "20 – 25%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Current Affairs", icon: "📰", type: "current_affairs" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt66: {
                  studyNotes: [
                    { id: "sn-subt66-1", title: "RBI and its Functions" },
                    { id: "sn-subt66-2", title: "Monetary Policy & Repo Rates" },
                  ],
                  pyqCount: 20,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                    { id: "sbi-po-mock-fl-02", name: "SBI PO Prelims — Mock Test 02" },
                  ],
                },
                subt67: {
                  studyNotes: [
                    { id: "sn-subt67-1", title: "Banking Terminology (NPA, CASA, CRR, SLR)" },
                    { id: "sn-subt67-2", title: "Types of Banks & Financial Institutions" },
                  ],
                  pyqCount: 18,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                  ],
                },
                subt68: {
                  studyNotes: [
                    { id: "sn-subt68-1", title: "Government Banking Schemes" },
                    { id: "sn-subt68-2", title: "Capital Markets & SEBI" },
                    { id: "sn-subt68-3", title: "Payment Systems (UPI, NEFT, RTGS)" },
                  ],
                  pyqCount: 15,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-fl-02", name: "SBI PO Prelims — Mock Test 02" },
                  ],
                },
                subt69: {
                  studyNotes: [
                    { id: "sn-subt69-1", title: "Insurance Basics (IRDAI)" },
                  ],
                  pyqCount: 8,
                  practiceCount: 5,
                  mockTests: [],
                },
                subt70: {
                  studyNotes: [
                    { id: "sn-subt70-1", title: "Recent Banking News & Appointments" },
                  ],
                  pyqCount: 10,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Banking and Financial Awareness covers RBI and its Functions, Monetary Policy and Repo Rates, Banking Terminology (NPA, CASA, CRR, SLR), Types of Banks and Financial Institutions, Government Banking Schemes, Capital Markets and SEBI, Insurance Basics (IRDAI), Payment Systems (UPI, NEFT, RTGS), and Recent Banking News and Appointments.",
              keyPointsToFocus: [
                "RBI policies, repo rate, and reverse repo rate are always asked",
                "Banking terminology (NPA, CASA, CRR, SLR) must be memorised",
                "Recent banking news from last 6 months is critical for Mains",
                "Government schemes related to banking (PM Jan Dhan, Mudra) are important",
              ],
              importantSubtopics: ["subt66", "subt67", "subt68"],
            },
            {
              topicId: "t45", // Economy
              subtopics: [],
              weightage: "10 – 15%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Current Affairs", icon: "📰", type: "current_affairs" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                "t45": {
                  studyNotes: [
                    { id: "sn-t45-1", title: "Basic Economic Concepts" },
                    { id: "sn-t45-2", title: "GDP, GNP & Inflation" },
                    { id: "sn-t45-3", title: "Union Budget Highlights" },
                    { id: "sn-t45-4", title: "International Economic Organisations" },
                  ],
                  pyqCount: 15,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                    { id: "sbi-po-mock-fl-02", name: "SBI PO Prelims — Mock Test 02" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Economy covers Basic Economic Concepts, GDP and GNP, Union Budget Highlights, Economic Survey Key Points, Inflation and Deflation, Five-Year Plans and NITI Aayog, Foreign Trade and Balance of Payments, International Economic Organisations (IMF, World Bank, WTO), and Indian Economic Reforms.",
              keyPointsToFocus: [
                "Union Budget and Economic Survey highlights are must-read",
                "GDP, inflation, and fiscal deficit figures are frequently asked",
                "International organisations (IMF, World Bank) and their roles",
                "Economic terms like CAD, FDI, FPI must be clearly understood",
              ],
              importantSubtopics: [],
            },
          ],
        },
        {
          sectionId: "sec3", // English Language
          topics: [
            {
              topicId: "t35", // Reading Comprehension
              subtopics: ["subt53", "subt54"],
              weightage: "25 – 30%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt53: {
                  studyNotes: [
                    { id: "sn-subt53-m1", title: "Long & Abstract Passages" },
                    { id: "sn-subt53-m2", title: "Inference & Implication Questions" },
                    { id: "sn-subt53-m3", title: "Author\'s Tone & Purpose" },
                  ],
                  pyqCount: 30,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-en-01", name: "English Language — Sectional Test 01" },
                    { id: "sbi-po-mock-fl-01", name: "SBI PO Prelims — Mock Test 01" },
                    { id: "sbi-po-mock-fl-02", name: "SBI PO Prelims — Mock Test 02" },
                  ],
                },
                subt54: {
                  studyNotes: [
                    { id: "sn-subt54-m1", title: "Vocabulary in Context" },
                    { id: "sn-subt54-m2", title: "Critical Reasoning from Passage" },
                  ],
                  pyqCount: 20,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-en-01", name: "English Language — Sectional Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Mains Reading Comprehension covers Long and Abstract Passages (editorial and economic themes), Inference and Implication Questions, Author's Tone and Purpose, Vocabulary in Context, Passage Summary Questions, and Critical Reasoning based on passage arguments.",
              keyPointsToFocus: [
                "Mains RC passages are longer and more abstract — practice editorial passages",
                "Focus on understanding the central argument, not just facts",
                "Vocabulary questions test contextual usage — read widely",
                "Critical reasoning questions require evaluating argument strength",
              ],
              importantSubtopics: ["subt53", "subt54"],
            },
            {
              topicId: "t39", // Para Jumbles
              subtopics: ["subt64", "subt65"],
              weightage: "10 – 15%",
              studyResources: [
                { label: "Study Notes", icon: "📖", type: "notes" },
                { label: "PYQP", icon: "📄", type: "pyqp" },
                { label: "Practice Questions", icon: "✏️", type: "practice" },
                { label: "Mock Tests", icon: "🎯", type: "mock" },
              ],
              subtopicResources: {
                subt64: {
                  studyNotes: [
                    { id: "sn-subt64-1", title: "Sentence Rearrangement (4–6 sentences)" },
                    { id: "sn-subt64-2", title: "Odd Sentence Out" },
                  ],
                  pyqCount: 12,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-en-01", name: "English Language — Sectional Test 01" },
                  ],
                },
                subt65: {
                  studyNotes: [
                    { id: "sn-subt65-1", title: "Paragraph Completion" },
                    { id: "sn-subt65-2", title: "Coherence-based Sentence Ordering" },
                  ],
                  pyqCount: 10,
                  practiceCount: 5,
                  mockTests: [
                    { id: "sbi-po-mock-sec-en-01", name: "English Language — Sectional Test 01" },
                  ],
                },
              } as Record<string, SubtopicResources>,
              syllabus:
                "Para Jumbles and Sentence Rearrangement covers Sentence Rearrangement (4–6 sentences), Odd Sentence Out, Paragraph Completion (finding the concluding or opening sentence), and Coherence-based Sentence Ordering.",
              keyPointsToFocus: [
                "Identify the opening sentence first — it sets the context",
                "Look for pronoun references and connectors to link sentences",
                "Odd sentence out requires finding the sentence that breaks the theme",
                "Practice with editorial paragraphs to improve paragraph sense",
              ],
              importantSubtopics: ["subt64", "subt65"],
            },
          ],
        },
      ],
    },
  ],
};
